import { Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
    AppInformationService,
    AuthService,
    EntitiesService,
    GenesisUnitOfWork,
    InvoiceService,
    ReportService,
    SessionInformationService,
    UtilityService,
} from 'app/core/core.module';
import { AssemblySchedule, Confirmation, Delivery, DeliveryOption, InvoiceStatus, Order, OrderLineDetail, OrderLineStatus, PackingSlip, Part } from 'app/core/model/entity-model';
import { InputDialogComponent } from 'app/shared/input-dialog/input-dialog.component';
import { SharedModule } from 'app/shared/shared.module';
import { DxDataGridComponent } from 'devextreme-angular';
import { alert, confirm } from 'devextreme/ui/dialog';
import _ from 'lodash';
import moment from 'moment';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';

import { OrderOptionDialogComponent } from 'app/orders/order-option-dialog/order-option-dialog.component';
import { StatusUpdateDialogComponent } from 'app/orders/status-update-dialog/status-update-dialog.component';
import { DeliveryFlyoutComponent } from '../delivery-flyout/delivery-flyout.component';
import { PackingSlipDialogComponent } from 'app/packing-slips/packing-slip-dialog/packing-slip-dialog.component';

@Component({
    selector: 'app-scheduling-overview',
    templateUrl: './scheduling-overview.component.html',
    styleUrls: ['./scheduling-overview.component.scss'],
    standalone: false,
})
export class SchedulingOverviewComponent implements OnInit, OnDestroy {
    result: Subject<any> = new Subject<any>();
    orderLineDetails: any;
    filter: any = {};
    previousInvoice = '';
    showCompleted = false;
    showAsDialog = false;
    processing = false;
    orderId: any;
    canRemoveFromPackSlip = false;
    globalFilter: any;
    canSetBuilt = false;
    canSetUnBuilt = false;
    canAllocateStock = false;
    gridStorageKey: string;
    filterStorageKey: string;
    mode: string;
    assemblyMode: boolean = false;
    shippingMode: boolean = false;
    startOfDay: Date;
    endOfDay: Date;
    showStackFlyout = false;
    canMakeDeliveries = false;
    isFlyoutReadOnly = false;
    openedDelivery: Delivery;

    @ViewChild('dataGrid') dataGrid: DxDataGridComponent;
    @ViewChild('deliveryFlyout') deliveryFlyout: DeliveryFlyoutComponent;
    onOptionChangedShared = SharedModule.onOptionChanged;

    constructor(
        protected uow: GenesisUnitOfWork,
        private router: Router,
        public auth: AuthService,
        private modalService: BsModalService,
        private appInfo: AppInformationService,
        private reportService: ReportService,
        private entitiesService: EntitiesService,
        private utility: UtilityService,
        private route: ActivatedRoute,
        private sessionInfo: SessionInformationService,
        private invoiceService: InvoiceService,
    ) {}

    async ngOnInit() {
        this.startOfDay = moment().startOf('day').toDate();
        this.endOfDay = moment().endOf('day').toDate();
        this.mode = this.route.snapshot.data['mode'];
        this.assemblyMode = this.mode === 'assembly';
        this.shippingMode = this.mode === 'shipping';
        this.gridStorageKey = `${this.mode}-schedule-grid`;
        this.filterStorageKey = `${this.mode}-schedule-filter`;
        const deliveryOptions = this.uow.lookups.deliveryOptions;
        const dealers = await this.uow.dealers.all();
        this.filter = this.sessionInfo.getItem(this.filterStorageKey) ?? { endDate: moment(new Date()).isoWeekday(6).startOf('day').toDate() };

        const users = await this.uow.users.all();
        this.setPermissions();
        await this.getData(false);
    }

    ngOnDestroy() {
        this.uow.rollback();
    }

    // @HostListener allows us to also guard against browser refresh, close, etc.
    @HostListener('window:beforeunload')
    canDeactivate() {
        return !this.hasChanges();
    }

    setPermissions() {
        this.canSetBuilt = this.auth.hasRole('BuildDetail:SetToBuilt');
        this.canSetUnBuilt = this.auth.hasRole('BuildDetail:SetToUnBuilt');
        this.canAllocateStock = this.auth.hasRole('BuildDetail:AllocateStock');
        this.canMakeDeliveries = this.auth.hasRole('Delivery');
    }

    async search() {
        this.appInfo.pleaseWaitShow();
        await this.getData();
        this.appInfo.pleaseWaitHide();
    }

    clear() {
        this.orderLineDetails = [];
        this.filter = {};
        this.sessionInfo.removeItem(this.filterStorageKey);
    }

    //customize the columns on the first load of the screen assembly vs shipping
    customizeColumns = (columns) => {
        columns.forEach((col) => {
            if (col.dataField === 'buildDue') {
                col.sortIndex = this.assemblyMode ? 1 : null;
                col.sortOrder = this.assemblyMode ? 'asc' : null;
            }
            if (col.dataField === 'dueDate') {
                col.sortIndex = this.shippingMode ? 1 : null;
                col.sortOrder = this.shippingMode ? 'asc' : null;
            }
        });
    };

    async getData(showCompleted?: boolean) {
        // const orderLines = await this.uow.getActiveOrderLines();
        // const packinSlips = await this.uow.packingSlips.all(); // 2/7/23 MR: commented out because of expand lower

        let orderLineDetails = null;

        orderLineDetails = (await this.uow.getOrderBuildDetailByDates(this.filter, this.mode === 'assembly' ? 'buildDue' : 'dueDate', [
            'status',
            'packingSlip',
            'orderLine.order.achPayments',
            'orderLine.order.confirmations',
            'optionDetails.orderLine',
            'notes',
            'deliveryStacks',
        ])) as OrderLineDetail[];

        await this.entitiesService.setShipAddressNotes(orderLineDetails);

        if (this.mode === 'assembly') {
            this.orderLineDetails = orderLineDetails.filter((x) => {
                const dueDate = moment(x.buildDue).startOf('day').toDate();
                if (x.status.countAsWIP) return x;
            });
        } else {
            this.orderLineDetails = orderLineDetails.filter((x) => {
                const dueDate = moment(x.buildDue).startOf('day').toDate();
                const deliveryId = x.deliveryOptionId;
                if (
                    x.statusId !== OrderLineDetail.Statuses.Complete &&
                    x.statusId !== OrderLineDetail.Statuses.DeliveredPickedUp &&
                    deliveryId !== DeliveryOption.Options.UPS
                    // deliveryId !== DeliveryOption.Options.CPU &&
                    // deliveryId !== DeliveryOption.Options.DPU
                )
                    return x;
            });
        }

        // noteHTML
        this.entitiesService.setNoteHtml(this.orderLineDetails);

        // optionHTML
        this.entitiesService.setOptionHtml(this.orderLineDetails);

        // confirmation
        this.orderLineDetails.forEach((detail) => {
            if (!detail.orderLine.order.confirmations) return;

            const confirmations = detail.orderLine.order.confirmations.filter(
                (confirmation: Confirmation) => !confirmation.salesRepConfirmation && !confirmation.deleted && confirmation.approved,
            );
            if (confirmations && confirmations.length > 0) {
                const approvedDate = _(confirmations).orderBy('created', 'desc').valueOf()[0].approved;
                detail.confirmationApproved = approvedDate;
            }
        });

        this.sessionInfo.setItem(this.filterStorageKey, this.filter);
    }

    hasChanges() {
        return this.uow.hasChanges();
    }

    async save() {
        await this.uow.commit();
    }

    async saveDialog() {
        await this.dataGrid.instance.saveEditData();

        // check for duplicates in the same order
        const vins = this.orderLineDetails
            .map((x) => {
                return x.vIN;
            })
            .filter((x) => {
                return x.length > 8;
            });
        // console.log('vins', vins);

        const duplicateVins = vins.filter((e, i, a) => a.indexOf(e) !== i);
        if (duplicateVins.length !== 0) {
            await alert(`There are duplicate VINs on the Sales Order\r\n${duplicateVins}`, `${this.appInfo.settings.company}`);
            return;
        }

        if (vins.length !== 0) {
            // check for duplicate VINs on existing orders
            const vinIds = this.orderLineDetails.map((x) => {
                return x.id;
            });
            const duplicates = (await this.utility.duplicateVins(vins, vinIds)) as any;
            // console.log('duplicates', duplicates);
            const count = duplicates.length;
            if (count !== 0) {
                await alert(
                    `The ${count > 1 ? 'VINs ' + duplicates + ' are' : 'VIN - ' + duplicates + ' - is'} used on an existing Sales Order`,
                    `${this.appInfo.settings.company}`,
                );
                return;
            }
        }
        await this.save();
        this.closeDialog();
    }

    closeDialog() {
        if (this.hasChanges()) {
            this.dataGrid.instance.cancelEditData();
            this.uow.rollback();
        }
        if (this.showAsDialog) this.result.next({});
    }

    async onRowUpdating(e) {
        this.processing = true;
    }
    async onRowUpdated(e) {
        this.processing = false;
    }

    async toggleCompleted() {
        this.showCompleted = !this.showCompleted;
        await this.getData(this.showCompleted);
    }

    onCellPrepared(e) {
        if (e.rowType === 'data') {
            const isStacked = e.data.deliveryId;
            if (isStacked) {
                if (e.column.dataField === 'deliveryId') {
                    console.log('e', e);
                    if (e.cellElement) {
                        e.cellElement.className = e.cellElement.className + ' stacked';
                    }
                }
            }
        }
    }

    async rowPrepare(e) {
        if (e.rowType === 'data' && !!e.data.orderLine && !!e.data.orderLine.order && !!e.data.orderLine.order.dealer) {
            if (e.data.orderLine.order.dealer.colorCode === 'blue') {
                e.rowElement.className = e.rowElement.className + ' blue-line';
            } else if (e.data.orderLine.order.dealer.colorCode === 'red') {
                e.rowElement.className = e.rowElement.className + ' red-line';
            } else if (e.data.orderLine.order.dealer.colorCode === 'green') {
                e.rowElement.className = e.rowElement.className + ' green-line';
            } else if (e.data.orderLine.order.dealer.colorCode === 'orange') {
                e.rowElement.className = e.rowElement.className + ' orange-line';
            } else if (e.data.orderLine.order.dealer.colorCode === 'white') {
                e.rowElement.className = e.rowElement.className + ' white-line';
            } else if (e.data.orderLine.order.dealer.colorCode === 'gray') {
                e.rowElement.className = e.rowElement.className + ' gray-line';
            }
        }
        if (e.rowType === 'data') {
            if (this.assemblyMode) {
                const deliveryId = e.data.deliveryOptionId;

                if (deliveryId === DeliveryOption.Options.CPU) {
                    e.cells.forEach((cell) => {
                        if (cell.column.dataField === 'deliveryOption.name') {
                            console.log('delivery');
                            if (cell.cellElement) {
                                cell.cellElement.className = cell.cellElement.className + ' customer-pickup';
                            }
                        }
                    });
                }
                if (e.data.buildDue && e.data.buildDue < this.startOfDay) {
                    e.cells.forEach((cell) => {
                        if (cell.column.dataField === 'buildDue') {
                            if (cell.cellElement) {
                                cell.cellElement.className = cell.cellElement.className + ' past-due';
                            }
                        }
                    });
                }
                if (e.data.buildDue && e.data.buildDue > this.startOfDay && e.data.buildDue < this.endOfDay) {
                    e.cells.forEach((cell) => {
                        if (cell.column.dataField === 'buildDue') {
                            if (cell.cellElement) {
                                cell.cellElement.className = cell.cellElement.className + ' due-today';
                            }
                        }
                    });
                }
            }
            if (this.shippingMode) {
                const countAsWIP = e.data.status.countAsWIP;
                const isStacked = e.data.deliveryId;

                if (countAsWIP) {
                    e.cells.forEach((cell) => {
                        if (cell.column.dataField === 'status.name') {
                            if (cell.cellElement) {
                                cell.cellElement.className = cell.cellElement.className + ' new-status-on-delivery';
                            }
                        }
                    });
                }
                if (isStacked) {
                    e.cells.forEach((cell) => {
                        if (cell.column.dataField === 'deliveryId') {
                            // console.log(cell);
                            // console.log(cell.cellElement);
                            if (cell.cellElement) {
                                cell.cellElement.className = cell.cellElement.className + ' stacked';
                            }
                        }
                    });
                }
                if (e.data.dueDate && e.data.dueDate < this.startOfDay) {
                    e.cells.forEach((cell) => {
                        if (cell.column.dataField === 'dueDate') {
                            if (cell.cellElement) {
                                cell.cellElement.className = cell.cellElement.className + ' past-due';
                            }
                        }
                    });
                }
                if (e.data.dueDate && e.data.dueDate > this.startOfDay && e.data.dueDate < this.endOfDay) {
                    e.cells.forEach((cell) => {
                        if (cell.column.dataField === 'dueDate') {
                            if (cell.cellElement) {
                                cell.cellElement.className = cell.cellElement.className + ' due-today';
                            }
                        }
                    });
                }
            }
            // e.cells.forEach((cell) => {
            //     if (cell.column.dataField === 'amount') {
            //         if (cell.cellElement) {
            //             cell.cellElement.className = cell.cellElement.className + ' amount-cell';
            //             if (cell.value === 0) {
            //                 cell.cellElement.className = cell.cellElement.className + ' zero-amount';
            //             }
            //             if (cell.value < 0) {
            //                 cell.cellElement.className = cell.cellElement.className + ' overpayment';
            //             }
            //         }
            //     }
            //     if (cell.column.dataField === 'parentBalance') {
            //         if (cell.cellElement && cell.value === 0) cell.cellElement.className = cell.cellElement.className + ' zero-balance';
            //     }
            // });
        }
    }

    getHTMLOptions(cellInfo) {
        if (!cellInfo || !cellInfo.options || !cellInfo.options.includes('<p>')) return;
        let rows = '';
        const data = cellInfo.options.split('<p>');
        data.forEach((field) => {
            rows += `${field.indexOf('-DELETED') !== -1 ? '<s><font color="red">' : ''}${field || ''}${field.indexOf('-DELETED') !== -1 ? '</s></font color="red">' : ''}<br>`;
        });
        return `${rows}`;
    }

    async printPDF(cell: any) {
        if (cell.column.dataField === 'packingSlip.packingSlipNumber') {
            const reportGuid = ((await this.uow.getPackSlipByNumber(cell.value)) as any).reportGuid;
            this.reportService.openPackingSlip([reportGuid]);
        }
        if (cell.column.dataField === 'packingSlip.invoiceNumber') {
            const reportGuid = ((await this.uow.getPackSlipByInvoiceNumber(cell.value)) as any).reportGuid;
            this.reportService.openInvoiceReport([reportGuid]);
        }
    }

    async addToPackSlip() {
        const selectedRows = this.dataGrid.instance.getSelectedRowsData();
        if (selectedRows.length === 0) return;

        const existingPackSlips = await this.uow.getDistinctPackSlips();
        const dialogValueArray = existingPackSlips.map((x) => {
            return {
                id: x.packingSlipNumber,
                name: `${x.packingSlipNumber} - ${x.dealer_CompanyName}`,
            };
        });

        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: 'Add To Packing Slip',
                dialogPrompt: 'Enter Slip Number',
                checkNumeric: true,
                checkInteger: true,
                mode: 'selectWithId',
                dialogValueArray: dialogValueArray,
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.result && !!result.value) {
                const packSlip = await this.uow.getPackSlipByNumber(result.value);
                if (!packSlip) {
                    await alert(`Packing Slip # ${result.value} does not exist`, `${this.appInfo.settings.company}`);
                    return;
                }
                this.appInfo.pleaseWaitShow();
                const packSlipDeliveryOption = this.uow.lookups.deliveryOptions?.find((d: any) => d.name === packSlip.deliveryMethod);
                let detailIds = [];
                selectedRows.forEach((row) => {
                    row.statusId = OrderLineDetail.Statuses.PackSlipCreated;
                    row.packingSlipId = packSlip.id;
                    if (packSlipDeliveryOption) row.deliveryOptionId = packSlipDeliveryOption.id;
                    detailIds.push(row.id);
                    row.optionDetails.forEach((option) => {
                        option.statusId = OrderLineDetail.Statuses.PackSlipCreated;
                        option.packingSlipId = packSlip.id;
                        if (packSlipDeliveryOption) option.deliveryOptionId = packSlipDeliveryOption.id;
                        detailIds.push(option.id);
                    });
                    this.entitiesService.createOrderLineDetailNote(row.id, `Added to pack slip #${result.value}`, this.auth.currentUser.id);
                });
                await this.save();
                await this.utility.setPaymentLinePackSlipId(detailIds, packSlip.id);
                this.appInfo.pleaseWaitHide();
                this.dataGrid.instance.clearSelection();
            }
            modalRef.hide();
        });
    }

    async removeFromPackSlip() {
        const selectedRows = this.dataGrid.instance.getSelectedRowsData();
        if (selectedRows.length === 0) return;

        await this.invoiceService.removeFromPackSlip(selectedRows);
        this.dataGrid.instance.clearSelection();
    }

    async printPackList() {
        const selectedRows = this.dataGrid.instance.getSelectedRowsData();
        if (selectedRows.length === 0) return;

        if (
            selectedRows.filter((x) => {
                return x.statusId === OrderLineDetail.Statuses.New;
            }).length !== 0
        ) {
            const result = await confirm(`One or more of the selected trailers is on status NEW. Continue?`, `${this.appInfo.settings.company}`);
            if (!result) return;
        }

        const shipToCount = _.uniq(
            selectedRows.map((x) => {
                return x.orderLine.order.shipToAddress1;
            }),
        ).length;
        if (shipToCount > 1) {
            await alert('There are multiple shipTo addresses selected', `${this.appInfo.settings.company}`);
            return;
        }

        const packSlips = _.uniq(
            selectedRows.map((x) => {
                return x.packingSlipId;
            }),
        );
        if (packSlips.length > 1) {
            await alert('There are multiple packing slips selected', `${this.appInfo.settings.company}`);
            return;
        }

        //CHECK FOR PAYMENTS
        let detailIds = [];
        let total = 0;
        selectedRows.forEach((row) => {
            detailIds.push(row.id);
            total += row.quantity * row.price;
            row.optionDetails.forEach((option) => {
                detailIds.push(option.id);
                total += option.quantity * option.price;
            });
        });
        const payments = await this.utility.checkForPayments(detailIds);
        // console.log(payments);

        const dealer = selectedRows[0].orderLine.order.dealer;
        const freightCheck = selectedRows[0].deliveryOptionId === Order.DeliveryOptions.UPS;
        const defaultFreight = !!freightCheck ? selectedRows[0].orderLine.order.freight : 0;
        const notes = selectedRows[0].orderLine.order.shippingNote;
        if (packSlips.length === 1 && !packSlips[0]) {
            const modalRef = this.modalService.show(PackingSlipDialogComponent, {
                class: 'modal-sm',
                ignoreBackdropClick: true,
                keyboard: false,
                initialState: {
                    freight: defaultFreight,
                    notes: notes,
                    dealer: dealer,
                    selectedRows: selectedRows,
                    mode: 'edit',
                },
            });

            modalRef.content.result.subscribe(async (result) => {
                if (result.success) {
                    this.appInfo.pleaseWaitShow();
                    const packSlipDeliveryOption = this.uow.lookups.deliveryOptions?.find((d: any) => d.name === result.packingSlip.deliveryMethod);
                    selectedRows.forEach((row) => {
                        row.packingSlipId = result.packingSlip.id;
                        if (packSlipDeliveryOption) row.deliveryOptionId = packSlipDeliveryOption.id;
                        row.optionDetails.forEach((option) => {
                            option.packingSlipId = result.packingSlip.id;
                            if (packSlipDeliveryOption) option.deliveryOptionId = packSlipDeliveryOption.id;
                        });

                        row.statusId = OrderLineDetail.Statuses.PackSlipCreated;
                        row.optionDetails.forEach((option) => {
                            option.statusId = OrderLineDetail.Statuses.PackSlipCreated;
                        });
                        this.entitiesService.createOrderLineDetailNote(row.id, `Created pack slip #${result.packingSlip.packingSlipNumber}`, this.auth.currentUser.id);
                    });
                    const packSlip = (await this.uow.packingSlips.byId(result.packingSlip.id)) as PackingSlip;
                    packSlip.invoiceNumber = (await this.utility.getNextInvoiceNumber()) as any;
                    packSlip.invoiceStatusId = payments.hasPaymentLines
                        ? payments.total === total + packSlip.freight
                            ? InvoiceStatus.Statuses.Paid
                            : InvoiceStatus.Statuses.PartiallyPaid
                        : InvoiceStatus.Statuses.New;
                    await this.uow.commit();

                    //set the packSlipId of the paymentLines for the selected OrderLineDetails if there were payments
                    if (payments.hasPaymentLines) {
                        await this.utility.setPaymentLinePackSlipId(detailIds, packSlip.id);
                    }

                    this.reportService.openPackingSlip([result.packingSlip.reportGuid]);
                    await this.getData();
                    this.appInfo.pleaseWaitHide();
                }
                modalRef.hide();
            });
        } else {
            const packingSlipIds = selectedRows.map((x) => x.packingSlipId);
            const reportGuids = await this.uow.getReportGuidsByInvoiceIds(packingSlipIds);
            this.reportService.openPackingSlip(reportGuids);
        }
    }

    async addNote() {
        const selectedRows = this.dataGrid.instance.getSelectedRowsData();
        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: 'Build Note',
                dialogPrompt: 'Enter Note',
                checkNumeric: false,
                checkInteger: false,
                mode: 'text',
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.result && !!result.value) {
                this.appInfo.pleaseWaitShow();
                await this.entitiesService.addNote(selectedRows, result.value, true);
                await this.save();
                await this.getData();
                this.appInfo.pleaseWaitHide();
                this.dataGrid.instance.clearSelection();
            }
            modalRef.hide();
        });
    }

    async changeDueDate() {
        const selectedRows = this.dataGrid.instance.getSelectedRowsData();

        let initialDueDate = null;
        if (this.assemblyMode) {
            if (selectedRows.length === 1 || _.every(selectedRows, { buildDue: selectedRows[0].buildDue })) initialDueDate = selectedRows[0].buildDue;
        }
        if (this.shippingMode) {
            if (selectedRows.length === 1 || _.every(selectedRows, { dueDate: selectedRows[0].dueDate })) initialDueDate = selectedRows[0].dueDate;
        }

        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: `${this.assemblyMode ? 'Build' : 'Shipping'} Due Date`,
                dialogPrompt: 'Enter Date',
                dialogValue: initialDueDate,
                checkNumeric: false,
                checkInteger: false,
                mode: 'date',
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.result && !!result.value) {
                this.appInfo.pleaseWaitShow();
                for (let index = 0; index < selectedRows.length; index++) {
                    const row = selectedRows[index];
                    if (this.assemblyMode) {
                        row.buildDue = result.value;
                        if (row.buildDue && !row.scheduled) row.scheduled = row.buildDue;
                    }
                    if (this.shippingMode) row.dueDate = result.value;
                    const note = `${this.assemblyMode ? 'Build' : 'Shipping'} Due date changed to ${moment(result.value).format('MM/DD/YYYY')}`;
                    await this.entitiesService.createOrderLineDetailNote(row.id, note, this.auth.currentUser.id);
                }

                await this.save();
                await this.getData();
                this.appInfo.pleaseWaitHide();
                this.dataGrid.instance.clearSelection();
            }
            modalRef.hide();
        });
    }

    async multipleOrderLineQuantityCheck(orderLineDetailId: number): Promise<any> {
        return await this.utility.multipleOrderLineQuantityCheck(orderLineDetailId);
    }

    async splitOrderLines(orderLineDetailId: number): Promise<boolean> {
        const confirmSplit = await confirm(
            `The selected OrderLineDetail is part of a multi-quantity OrderLine.  Adding or deleting options will split this detail to its own OrderLine.
            Continue?

        If you wish to globally add or remove an option from the entire OrderLine, you can do so from the Order Detail screen.
        `,
            `${this.appInfo.settings.company}`,
        );

        if (!confirmSplit) return false;
        else {
            this.appInfo.pleaseWaitShow();
            const splitResult = await this.utility.splitOrderLinesBasedOnDetailEdit(orderLineDetailId);
            this.appInfo.pleaseWaitHide();
            return splitResult;
        }
    }

    async editOptions() {
        const selectedRow = this.dataGrid.instance.getSelectedRowsData()[0];

        //check if multiple OrderLine or single
        const multiple = await this.multipleOrderLineQuantityCheck(selectedRow.id);
        let oldEditSalesOrder = false;
        if (multiple.orderLineQtyGreaterThanOne && !multiple.hasNotes && !multiple.hasNegativeChildren) {
            //if multiple - ask to split it
            const splitCheck = await this.splitOrderLines(selectedRow.id);
            if (!splitCheck) return;
            await alert(`The OrderLines have been split.  You may now edit options`, `${this.appInfo.settings.company}`);
            // this.dataGrid.instance.clearSelection();
            await this.getData();
            return;
        } else if (multiple.orderLineQtyGreaterThanOne && (multiple.hasNotes || multiple.hasNegativeChildren)) {
            //a Sales Order that was edited by the old code
            await alert(
                `The selected OrderLineDetail belongs to an OrderLine that was already modified by the old program, either by itself or one of its siblings. `,
                `${this.appInfo.settings.company}`,
            );
            oldEditSalesOrder = true;
        }

        // console.log('selectedRow', selectedRow);
        const options = await this.uow.getOptionsForOrderLineDetail(selectedRow.id);
        // console.log('options', options);
        const availableOptions = ((await this.uow.getPartOptionsByPartId(selectedRow.orderLine.partId)) as any).map((x) => {
            return x.optionPart;
        });
        // console.log('available options', availableOptions);
        const modalRef = this.modalService.show(OrderOptionDialogComponent, {
            class: 'modal-md',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                options: options,
                availableOptions: availableOptions,
                orderLineDetail: selectedRow,
                deleteMode: oldEditSalesOrder ? 'old' : null,
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                this.appInfo.pleaseWaitShow();
                await this.getData();
                this.appInfo.pleaseWaitHide();
            }
            modalRef.hide();
        });
    }

    async editPackingSlip(orderLineDetail: OrderLineDetail) {
        if (!orderLineDetail || !orderLineDetail.packingSlip) return;

        const modalRef = this.modalService.show(PackingSlipDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                packingSlip: orderLineDetail.packingSlip,
                order: orderLineDetail.orderLine.order,
                mode: 'edit',
            },
        });
        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                this.appInfo.pleaseWaitShow();
                await this.getData();
                this.appInfo.pleaseWaitHide();
            }
            modalRef.hide();
        });
    }

    async getMiscDescription(): Promise<any> {
        this.appInfo.pleaseWaitHide();
        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: 'Add Miscellaneous Description',
                dialogPrompt: 'Enter Description',
                checkNumeric: false,
                checkInteger: false,
                mode: 'text',
            },
        });

        const promise = new Promise<any>(async (resolve, reject) => {
            modalRef.content.result.subscribe(async (result) => {
                if (result.result && !!result.value) {
                    resolve(result.value);
                }
                modalRef.hide();
                this.appInfo.pleaseWaitShow();
                resolve(null);
            });
        });
        return promise;
    }

    selectionChange(e) {
        const selectedRowData = this.dataGrid.instance.getSelectedRowsData();
        this.canRemoveFromPackSlip = selectedRowData.length > 0 && selectedRowData.every((a) => a.packingSlipId != null);
    }

    onToolbarPreparing(e) {
        e.toolbarOptions.items.unshift(
            {
                location: 'before',
                template: 'toolbarButtons',
            },
            {
                template: 'toolbarText',
                location: 'after',
            },
            {
                widget: 'dxButton',
                options: {
                    icon: 'revert',
                    elementAttr: { title: 'Reset Filters' },
                    onClick: () => {
                        e.component.clearFilter();
                    },
                },
                location: 'after',
            },
            {
                widget: 'dxButton',
                options: {
                    icon: 'refresh',
                    elementAttr: { title: 'Reset Grid Layout' },
                    onClick: () => {
                        e.component.state({});
                    },
                },
                location: 'after',
            },
        );
    }

    async openSchedulerBuilder(scheduled: Date) {
        this.router.navigate(['/schedule/build-scheduler', { scheduled: moment(scheduled).format('MM/DD/YYYY') }]);
    }

    async open(orderId: number) {
        const selectedRow = this.dataGrid.instance.getSelectedRowsData();
        if (selectedRow.length === 0) return;
        this.router.navigate(['/sales-orders/' + (!!selectedRow ? selectedRow[0].orderLine.order.id : 'new')]);
    }

    async rowDoubleClick(e) {
        if (!e.data || !e.data.id) return;
        await this.open(e.data.id);
    }

    async bulkUpdate() {
        const selectedRows = this.dataGrid.instance.getSelectedRowsData();
        if (selectedRows.length === 0) return;

        if (selectedRows.length > 1) {
            const success = await confirm(`Are you sure you want to bulk update the following?<br/><br/>${this.getDisplayNames(selectedRows)}`, this.appInfo.settings.company);
            if (!success) return;
        }

        const modalRef = this.modalService.show(StatusUpdateDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {},
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                this.appInfo.pleaseWaitShow();
                for (let index = 0; index < selectedRows.length; index++) {
                    const row = selectedRows[index];

                    //UPDATING STATUS
                    if (result.statusId && row.statusId !== result.statusId) {
                        row.statusId = result.statusId;
                        if (result.statusId === OrderLineDetail.Statuses.DeliveredPickedUp && !row.delivered) row.delivered = new Date();
                        let status = null;
                        Object.keys(OrderLineDetail.Statuses).forEach((key) => {
                            if (OrderLineDetail.Statuses[key] === result.statusId) {
                                status = key;
                            }
                        });
                        this.entitiesService.createOrderLineDetailNote(row.id, `Status Changed to ${status}`, this.auth.currentUser.id);
                        row.optionDetails.forEach((option) => {
                            option.statusId = result.statusId;
                            if (result.statusId === OrderLineDetail.Statuses.DeliveredPickedUp && !option.delivered) option.delivered = new Date();
                        });
                    }

                    //UPDATING DUE DATE
                    if (result.dueDate && this.auth.hasRole('BuildDetail:CanUpdateShipDue')) {
                        row.dueDate = result.dueDate;

                        row.optionDetails.forEach((option) => {
                            option.dueDate = result.dueDate;
                        });
                        this.entitiesService.createOrderLineDetailNote(row.id, `Due Date Changed to ${moment(result.dueDate).format('MM/DD/YYYY')}`, this.auth.currentUser.id);
                    }

                    // if (result.dueDate) {
                    //     if (this.assemblyMode) {
                    //         row.buildDue = result.dueDate;
                    //         if (row.buildDue && !row.scheduled) row.scheduled = row.buildDue;
                    //         this.entitiesService.createOrderLineDetailNote(row.id, `Build Due Changed to ${moment(result.dueDate).format('MM/DD/YYYY')}`, this.auth.currentUser.id);
                    //         row.optionDetails.forEach((option) => {
                    //             option.buildDue = result.dueDate;
                    //             if (option.buildDue && !option.scheduled) option.scheduled = option.buildDue;
                    //         });
                    //     }
                    //     if (this.shippingMode) {
                    //         row.dueDate = result.dueDate;
                    //         row.optionDetails.forEach((option) => {
                    //             option.dueDate = result.dueDate;
                    //         });
                    //         this.entitiesService.createOrderLineDetailNote(row.id, `Due Date Changed to ${moment(result.dueDate).format('MM/DD/YYYY')}`, this.auth.currentUser.id);
                    //     }
                    // }

                    if (result.buildDue && this.auth.hasRole('BuildDetail:CanUpdateBuildDue')) {
                        row.buildDue = result.buildDue;
                        row.optionDetails.forEach((option) => {
                            option.buildDue = result.buildDue;
                        });
                        this.entitiesService.createOrderLineDetailNote(row.id, `Build Due Changed to ${moment(result.buildDue).format('MM/DD/YYYY')}`, this.auth.currentUser.id);

                        if (this.assemblyMode) {
                            if (row.buildDue && !row.scheduled) row.scheduled = row.buildDue;
                            row.optionDetails.forEach((option) => {
                                if (option.buildDue && !option.scheduled) option.scheduled = option.buildDue;
                            });
                        }
                    }

                    //UPDATING DELIVERY OPTIONS
                    if (result.delivery && row.deliveryOptionId !== result.delivery.id) {
                        row.deliveryOptionId = result.delivery.id;
                        row.optionDetails.forEach((option) => {
                            option.deliveryOptionId = result.delivery.id;
                        });
                        this.entitiesService.createOrderLineDetailNote(row.id, `Delivery Changed to ${result.delivery.name}`, this.auth.currentUser.id);
                    }

                    //UPDATING NOTES
                    if (result.noteValue) {
                        this.entitiesService.createOrderLineDetailNote(row.id, result.noteValue, this.auth.currentUser.id, 2, true);
                    }
                }
                await this.uow.commit();

                // check if the whole order is complete and complete it if it isn't
                if (result.value === OrderLineDetail.Statuses.Complete) {
                    selectedRows.forEach(async (row) => {
                        await this.utility.setOrderComplete([row.orderLine.orderId]);
                    });
                }

                await this.getData();
                this.appInfo.pleaseWaitHide();
            }
            modalRef.hide();
        });
    }

    getDisplayNames(buildDetails: OrderLineDetail[]) {
        if (!buildDetails) return '';
        return buildDetails.map((detail) => `${detail.orderLine?.order?.dealer?.companyName} - ${detail.vIN ? detail.vIN : detail.orderLine?.partNumber}`).join('<br/>');
    }

    async delete() {
        const selectedRow = this.dataGrid.instance.getSelectedRowsData()[0];
        if (!selectedRow) return;
        const result = await (confirm(
            `Are you sure you want to DELETE this detail?\r\nThis will place all parts used to make this and its options back into inventory.`,
            `${this.appInfo.settings.company}`,
        ) as Promise<boolean>);
        if (!result) return;

        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: 'Reason for Deleting',
                dialogPrompt: 'Enter the reason you are deleting this trailer',
                checkNumeric: false,
                checkInteger: false,
                mode: 'text',
            },
        });

        const promise = new Promise<boolean>(async (resolve, reject) => {
            modalRef.content.result.subscribe(async (result) => {
                modalRef.hide();
                if (result.result) {
                    if (!result.value) {
                        alert('A reason is required in order to delete a trailer', this.appInfo.settings.company);
                        resolve(false);
                        return;
                    }

                    await this.entitiesService.addNote([selectedRow], `Delete Reason: ${result.value}`, true);
                    resolve(true);
                }
            });
        });
        const success = await promise;
        if (!success) return;

        this.appInfo.pleaseWaitShow();

        // add orderLine for negative
        if (!!selectedRow.orderLine.partId) {
            const part = (await this.uow.parts.byId(selectedRow.orderLine.partId)) as Part;
            const orderLine = this.entitiesService.addOrderLine(
                part,
                selectedRow.quantity * -1,
                null,
                selectedRow.orderLine.order,
                selectedRow.isReplacement ? 'Replacements' : '',
            );
        } else {
            const orderLine = this.entitiesService.addOrderLineForMisc(selectedRow.orderLine, selectedRow.quantity * -1, selectedRow.orderLine.order);
        }
        await this.utility.deleteOrderLineDetail(selectedRow.id);
        this.entitiesService.createOrderLineDetailNote(selectedRow.id, 'Deleted', this.auth.currentUser.id);
        selectedRow.deleted = new Date();

        // delete the options if there are any
        for (let index = 0; index < selectedRow.optionDetails.length; index++) {
            const element = selectedRow.optionDetails[index];

            if (!element.deleted) {
                // add orderLine for negative
                const optionPart = (await this.uow.parts.byId(element.orderLine.partId)) as Part;
                const optionOrderLine = this.entitiesService.addOrderLine(optionPart, element.quantity * -1, element.orderLine.id, element.orderLine.order);
                await this.utility.deleteOrderLineDetail(element.id);
                this.entitiesService.createOrderLineDetailNote(element.id, 'Deleted', this.auth.currentUser.id);
                element.deleted = new Date();
            }
        }

        await this.uow.commit();
        await this.getData();
        this.appInfo.pleaseWaitHide();
    }

    async onExporting(e) {
        SharedModule.onExportingGrid(e.component, this.entitiesService.optionsAndNotesCustomizeCellOverride);
    }

    async kickOffDetail(detailId: number) {
        const detail = this.dataGrid.instance.getSelectedRowsData()[0];
        if (!detail) return;
        this.appInfo.pleaseWaitShow();
        const orderLineDetailIds = [];
        orderLineDetailIds.push(detail.id);
        detail.optionDetails.forEach((option) => {
            orderLineDetailIds.push(option.id);
        });
        await this.utility.processOrderLineDetails(orderLineDetailIds);
        this.appInfo.pleaseWaitHide();
    }

    async printBuildList() {
        const selectedRows = this.dataGrid.instance.getSelectedRowsData();
        if (!selectedRows || selectedRows.length === 0) return;

        const ids = selectedRows.map((x) => {
            return x.id;
        });
        ids.forEach((id) => {
            const schedule = this.uow.assemblySchedules.createEntity() as AssemblySchedule;
            schedule.buildDate = new Date();
            schedule.created = new Date();
            schedule.createdBy = this.auth.currentUser.userName;
            schedule.jigId = 1;
            schedule.orderLineDetailId = id;
        });
        await this.uow.commit();
        await this.reportService.printAssemblySchedule({ ids: ids });
    }

    async setStatusToBuilt() {
        const selectedRows = this.dataGrid.instance.getSelectedRowsData();
        if (selectedRows.length === 0) return;

        if (selectedRows.length > 1) {
            const success = await confirm(
                `Are you sure you want to update the status for the following?<br/><br/>${this.getDisplayNames(selectedRows)}`,
                this.appInfo.settings.company,
            );
            if (!success) return;
        }

        const builtCheck = selectedRows.filter((x) => {
            return x.built || _.some(x.optionDetails, 'built');
        });
        if (builtCheck.length !== 0) {
            const builtDetails = [];
            builtCheck.forEach((x) => {
                builtDetails.push(x.id);
            });
            // await alert(`Some of the selected details have already been marked as Built.  Contact Administrator.\r\nError ${builtDetails}`,`${this.appInfo.settings.company}`);
            // return
        }

        this.appInfo.pleaseWaitShow();
        for (let index = 0; index < selectedRows.length; index++) {
            const row = selectedRows[index];
            await this.utility.updateInventory(row.id);

            this.entitiesService.createOrderLineDetailNote(row.id, `Status Changed to Built`, this.auth.currentUser.id);
            row.statusId = OrderLineStatus.OrderLineStatusOptions.Built;
            // row.built = new Date();  //done in the utility controller
            row.optionDetails.forEach((option) => {
                option.statusId = OrderLineStatus.OrderLineStatusOptions.Built;
                // option.built = new Date();  //done in the utility controller
            });
        }
        await this.uow.commit();
        this.dataGrid.instance.clearSelection();
        this.appInfo.pleaseWaitHide();
    }

    async toggleHotOrder() {
        const selectedRows = this.dataGrid.instance.getSelectedRowsData();
        if (selectedRows.length === 0) return;

        const markHot = !selectedRows.every((r) => r.hotOrder);
        selectedRows.forEach((r) => (r.hotOrder = markHot));
        await this.uow.commit();
        this.appInfo.pleaseWaitShow();
        await this.dataGrid.instance.refresh();
        this.dataGrid.instance.clearSelection();
        this.appInfo.pleaseWaitHide();
    }

    sortByHotOrder(rowData) {
        return rowData.hotOrder ? 1 : 0;
    }

    async markAsNotBuilt() {
        const selectedRow = this.dataGrid.instance.getSelectedRowsData()[0];
        if (!selectedRow) return;

        if (!selectedRow.built) {
            await alert(`Selected Row is not on Built status`, `${this.appInfo.settings.company}`);
            return;
        }

        const confirmResult = await confirm(
            `This will mark the selected line and all its options as UnBuilt, returning the parts to inventory.  Continue?`,
            `${this.appInfo.settings.company}`,
        );
        if (!confirmResult) return;

        this.appInfo.pleaseWaitShow();

        await this.utility.unBuildOrderLineDetail(selectedRow.id);
        this.entitiesService.createOrderLineDetailNote(selectedRow.id, `Marked as UnBuilt`, this.auth.currentUser.id);
        selectedRow.statusId = OrderLineStatus.OrderLineStatusOptions.New;
        selectedRow.built = null;

        for (let index = 0; index < selectedRow.optionDetails.length; index++) {
            const optionLine = selectedRow.optionDetails[index];

            if (!optionLine.deleted) {
                await this.utility.unBuildOrderLineDetail(optionLine.id);
                this.entitiesService.createOrderLineDetailNote(optionLine.id, `Marked as UnBuilt`, this.auth.currentUser.id);
                optionLine.statusId = OrderLineStatus.OrderLineStatusOptions.New;
                optionLine.built = null;
            }
        }
        await this.uow.commit();
        this.appInfo.pleaseWaitHide();
        await alert(`Done`, `${this.appInfo.settings.company}`);
    }

    printMsrp(orderLineDetails: OrderLineDetail[]) {
        if (!orderLineDetails || orderLineDetails.length === 0) return;

        const lineIds = _(orderLineDetails.map((detail) => detail.orderLineId))
            .uniq()
            .valueOf();
        this.reportService.openMsrpReport(lineIds);
    }

    async generateStack() {
        this.appInfo.pleaseWaitShow();
        await this.addToStack();
        this.showStackFlyout = true;
        this.appInfo.pleaseWaitHide();
    }

    async addToStack() {
        const orderLineDetails = this.dataGrid.instance.getSelectedRowsData() as OrderLineDetail[];
        if (this.isFlyoutReadOnly || !orderLineDetails || orderLineDetails.length === 0) return;

        const ids = orderLineDetails.map((detail) => detail.id);
        await this.deliveryFlyout.addToStack(ids);
        this.dataGrid.instance.clearSelection();
    }

    closeStack() {
        this.showStackFlyout = false;
        this.isFlyoutReadOnly = false;
        this.openedDelivery = null;
    }

    async editDelivery(deliveryId: number) {
        if (!deliveryId || this.uow.hasChanges()) return;

        const delivery = (await this.uow.deliveries.byId(deliveryId, 'deliveryStacks')) as Delivery;
        this.openedDelivery = delivery;
        this.isFlyoutReadOnly = !!delivery.completed;
        this.showStackFlyout = true;
    }
}
